/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 stage path (design-reference "Sales · Deal record"): the record's
 * stages as one row of segments. Same rules as components/CustomStepper,
 * which renders this under v2:
 * - stages before the current one are done (check mark);
 * - the current one is filled, green when it is the success stage and red
 *   when it is the failure stage;
 * - once a final stage is chosen, the opposite final stage is disabled;
 * - stages are clickable only when the page passes onStepClick.
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { Check } from "lucide-react";
import { v2 as t } from "styles/tokens/v2";

const TONE = {
  current: { bg: "#1565C8", fg: "#FFFFFF", border: "#1565C8" },
  success: { bg: "#15803D", fg: "#FFFFFF", border: "#15803D" },
  failure: { bg: "#B42318", fg: "#FFFFFF", border: "#B42318" },
  done: { bg: "#E6EFFB", fg: "#1453A0", border: "#E6EFFB" },
  todo: { bg: t.surface2, fg: t.text2, border: t.border },
  disabled: { bg: t.surface2, fg: "#8A97AB", border: t.border },
};

export function stageState(index, label, { activeStep, finalState, successStep, failureStep }) {
  const greyed =
    Boolean(finalState) &&
    ((label === successStep && finalState === failureStep) || (label === failureStep && finalState === successStep));
  if (greyed) return "disabled";
  if (finalState && label === finalState && label === successStep) return "success";
  if (finalState && label === finalState && label === failureStep) return "failure";
  if (index === activeStep) return "current";
  if (index < activeStep) return "done";
  return "todo";
}

export default function StagePath({ steps, activeStep, onStepClick, finalState, successStep, failureStep }) {
  // Always one row, as in the design. Each label stays on one line and its
  // segment grows to fit; long paths (cases have eight stages) get tighter
  // type, and when the row is wider than the card (phones) it scrolls
  // sideways rather than squeezing labels out of their segments.
  const dense = steps.length > 6;
  return (
    <Box
      component="ol"
      aria-label="Stage"
      sx={{
        display: "flex",
        gap: "6px",
        listStyle: "none",
        m: 0,
        p: 0,
        overflowX: "auto",
        scrollbarWidth: "thin",
      }}
    >
      {steps.map((label, index) => {
        const state = stageState(index, label, { activeStep, finalState, successStep, failureStep });
        const tone = TONE[state];
        const clickable = Boolean(onStepClick) && state !== "disabled";
        return (
          <Box component="li" key={label} sx={{ flex: "1 0 auto", display: "flex" }}>
            <ButtonBase
              onClick={clickable ? () => onStepClick(index) : undefined}
              disabled={!clickable}
              aria-current={state === "current" || state === "success" || state === "failure" ? "step" : undefined}
              sx={{
                width: "100%",
                minHeight: 40,
                px: dense ? 0.75 : 1.5,
                py: 0.75,
                gap: 0.75,
                borderRadius: "8px",
                border: `1px solid ${tone.border}`,
                bgcolor: tone.bg,
                color: tone.fg,
                fontFamily: "inherit",
                fontSize: dense ? 12.5 : 14,
                fontWeight: state === "todo" || state === "disabled" ? 500 : 600,
                textAlign: "center",
                whiteSpace: "nowrap",
                lineHeight: 1.25,
                cursor: clickable ? "pointer" : "default",
                "&.Mui-disabled": { color: tone.fg, cursor: state === "disabled" ? "not-allowed" : "default" },
                "&:hover": clickable && state === "todo" ? { borderColor: "#1565C8", color: t.text } : {},
                "&.Mui-focusVisible": { outline: `2px solid ${t.link}`, outlineOffset: 2 },
              }}
            >
              {state === "done" && <Check size={15} strokeWidth={2.5} aria-hidden="true" />}
              {label}
            </ButtonBase>
          </Box>
        );
      })}
    </Box>
  );
}

StagePath.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeStep: PropTypes.number.isRequired,
  onStepClick: PropTypes.func,
  finalState: PropTypes.string,
  successStep: PropTypes.string,
  failureStep: PropTypes.string,
};

StagePath.defaultProps = { onStepClick: undefined, finalState: null, successStep: null, failureStep: null };
